import { Link, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { CATEGORIES } from "@/lib/categories";
import { Brand } from "@/components/Brand";
import { useFillCount } from "@/lib/useFillCount";
import api from "@/lib/api";
import { Search, Menu, X, Lock, TrendingUp, TrendingDown } from "lucide-react";

const today = new Date().toLocaleDateString("it-IT", {
  weekday: "long", day: "numeric", month: "long",
});

const PENNANTS = [
  "pn-punta", "pn-festone", "pn-rondine", "pn-guelfa", "pn-dentello",
  "pn-zigzag", "pn-obliqua", "pn-ghibellina", "pn-doppia", "pn-torneo",
];

function MedievalBanner() {
  const count = useFillCount(64);
  return (
    <div className="mb-banner" aria-hidden="true" data-testid="medieval-banner">
      <div className="mb-rod" />
      <div className="mb-row">
        {Array.from({ length: count }).map((_, i) => {
          const c = CATEGORIES[i % CATEGORIES.length];
          return (
            <span key={i} className={`mb-flag ${PENNANTS[i % PENNANTS.length]}`}
              style={{ "--cat": c.hex, animationDelay: `${(i % 8) * 0.14}s` }} />
          );
        })}
      </div>
    </div>
  );
}

function Ticker() {
  const [items, setItems] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    const load = () => {
      api.get("/value-cards").then((r) => {
        const cards = r.data || [];
        const list = cards.map((c) => {
          const live = c.value_source === "ebay-live" ? c.live : null;
          return {
            name: c.name,
            category: c.category,
            mid: Number(live?.median ?? c.value_mid) || 0,
            live: Boolean(live),
            count: live?.count || 0,
            // fluttuazione reale: confronto fra rilevazioni della stessa fonte
            delta: typeof c.delta_7d === "number" ? c.delta_7d : null,
          };
        }).sort((a, b) => Number(b.live) - Number(a.live));
        setItems(list);
        const stamps = cards.map((c) => c.last_checked_at).filter(Boolean).sort();
        setUpdatedAt(stamps.length ? stamps[stamps.length - 1] : null);
      }).catch(() => {});
    };
    load();
    const t = setInterval(load, 10 * 60 * 1000); // i valori si aggiornano ogni ora
    return () => clearInterval(t);
  }, []);

  if (items.length === 0) return null;
  const loop = [...items, ...items];
  const liveCount = items.filter((i) => i.live).length;
  const hhmm = updatedAt
    ? new Date(updatedAt).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div className="border-t-2 border-dashed border-line bg-panel-hi overflow-hidden" data-testid="value-ticker">
      <div className="flex items-center">
        <span className="shrink-0 z-10 flex items-center gap-1.5 px-3 py-1.5 bg-stone-900 text-amber-200 font-meta text-[9px] uppercase tracking-[0.18em]"
          title={liveCount ? `${liveCount} schede con prezzi presi dagli annunci eBay in corso` : "Stime indicative"}
          data-testid="ticker-badge">
          <TrendingUp size={11} /> {liveCount ? "Prezzi eBay" : "Stime AI"}
          {hhmm && <span className="hidden sm:inline text-amber-200/60" data-testid="ticker-updated">· {hhmm}</span>}
        </span>
        <div className="marquee-track py-1.5">
          {loop.map((it, i) => {
            const up = it.delta !== null && it.delta >= 0;
            const Icon = up ? TrendingUp : TrendingDown;
            const cat = CATEGORIES.find((c) => c.slug === it.category);
            return (
              <span key={i} className="flex items-center gap-2 px-5 font-meta text-[11px] whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: cat?.hex }} />
                <span className="text-stone-700 font-medium">{it.name}</span>
                {it.mid > 0 && (
                  <span className={it.live ? "text-stone-900 font-semibold" : "text-stone-500"}
                    title={it.live ? `Mediana di ${it.count} annunci in vendita ora su eBay` : "Stima indicativa"}>
                    {it.live ? "" : "~"}{Number(it.mid).toLocaleString("it-IT")} €
                  </span>
                )}
                {it.live && (
                  <span className="px-1 border border-stone-300 text-[8px] uppercase tracking-[0.12em] text-stone-500">
                    live
                  </span>
                )}
                {it.delta !== null && (
                  <span className={`inline-flex items-center gap-0.5 font-bold ${up ? "text-emerald-600" : "text-rose-600"}`}
                    title="Variazione sugli ultimi 7 giorni, fra rilevazioni della stessa fonte">
                    <Icon size={11} />{up ? "+" : ""}{it.delta.toFixed(1)}%
                  </span>
                )}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function Masthead() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) window.location.href = `/cerca?q=${encodeURIComponent(q.trim())}`;
  };

  return (
    <header data-testid="header-masthead" className="sticky top-0 z-50 w-full bg-ink/95 backdrop-blur-xl">
      <MedievalBanner />

      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <button className="lg:hidden text-stone-900 p-1" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>

        <Link to="/" data-testid="brand-logo" className="flex-1 flex justify-center">
          <Brand size="xl" />
        </Link>

        <div className="flex items-center gap-2.5">
          <form onSubmit={submitSearch} className="hidden lg:flex items-center gap-2 bg-panel border-2 border-line rounded-full pl-4 pr-2 py-1.5 w-64 focus-within:border-stone-900 transition-colors">
            <Search size={15} className="text-stone-400" />
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca…"
              className="bg-transparent outline-none text-sm w-full text-stone-900 placeholder:text-stone-400"
            />
          </form>
          <Link data-testid="admin-link" to="/admin"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-stone-900 text-white px-3.5 py-2 font-meta text-[9px] uppercase tracking-[0.16em] hover:-translate-y-0.5 transition-transform sticker">
            <Lock size={10} /> Redazione
          </Link>
        </div>
      </div>

      <nav className="hidden lg:block">
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-3">
          <NavLink to="/" end className={({ isActive }) =>
            `nav-pill ${isActive ? "nav-pill-dark" : ""}`}>
            Home
          </NavLink>
          {CATEGORIES.map((c) => (
            <NavLink key={c.slug} to={`/categoria/${c.slug}`} data-testid={`category-nav-${c.slug}`}
              style={{ "--cat": c.hex }}
              className={({ isActive }) => `nav-pill nav-pill-cat ${isActive ? "is-active" : ""}`}>
              <span className="dot" />{c.short}
            </NavLink>
          ))}
          <NavLink to="/guide" className={({ isActive }) => `nav-pill ${isActive ? "nav-pill-dark" : ""}`}>Guide</NavLink>
          <NavLink to="/valori" data-testid="nav-valori" className={({ isActive }) => `nav-pill ${isActive ? "nav-pill-dark" : ""}`}>Valori</NavLink>
        </div>
      </nav>

      <div className="hidden lg:flex max-w-7xl mx-auto px-4 pb-2 items-center justify-between font-meta text-[9px] uppercase tracking-[0.18em] text-stone-400">
        <span className="capitalize">{today}</span>
        <span>Il portale simpatico dei collezionisti</span>
      </div>

      <Ticker />

      {open && (
        <nav className="lg:hidden border-t-2 border-dashed border-line bg-panel px-4 py-4">
          <form onSubmit={submitSearch} className="flex items-center gap-2 bg-ink border-2 border-line rounded-full px-4 py-2 mb-4">
            <Search size={15} className="text-stone-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…"
              className="bg-transparent outline-none text-sm w-full text-stone-900 placeholder:text-stone-400" data-testid="search-input-mobile" />
          </form>
          <Link to="/" onClick={() => setOpen(false)} className="block py-2 font-editorial font-bold text-lg text-stone-900">Home</Link>
          <div className="grid grid-cols-2 gap-2 my-3">
            {CATEGORIES.map((c) => (
              <Link key={c.slug} to={`/categoria/${c.slug}`} onClick={() => setOpen(false)}
                className="flex items-center gap-2 rounded-xl border-2 border-line bg-ink px-3 py-2.5 text-sm font-semibold text-stone-700">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: c.hex }} />{c.short}
              </Link>
            ))}
          </div>
          <Link to="/guide" onClick={() => setOpen(false)} className="block py-2 font-editorial font-bold text-lg text-stone-900">Guide</Link>
          <Link to="/valori" onClick={() => setOpen(false)} className="block py-2 font-editorial font-bold text-lg text-stone-900">Valori</Link>
          <Link to="/admin" onClick={() => setOpen(false)} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-stone-900 text-white px-4 py-2 font-meta text-[9px] uppercase tracking-[0.16em]">
            <Lock size={10} /> Area Redazione
          </Link>
        </nav>
      )}
    </header>
  );
}
